// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — shared primitives
// ═══════════════════════════════════════════════════════════════════════════
//
// This module holds the tiny schemas that every parameter / action / lookup
// table reuses:
//   - drawingPointSchema — 2D point ({ x, y })
//   - valueSetSchema     — Increment / List grip-snap constraint
//   - baseParameterSchema — fields every parameter kind carries
//   - baseActionSchema    — fields every action kind carries
//
// It is deliberately thin so parameter / action / lookup modules can import
// from one place without dragging in the full metadata wrapper. The file is
// imported indirectly by model.ts (via the dynamicBlock.ts barrel) so we
// redeclare drawingPointSchema here to avoid a circular import at module-
// init time (see note in the original dynamicBlock.ts).

import { z } from "zod";

// ─── Point ─────────────────────────────────────────────────────────
// NOTE: NOT imported from "./model" — dynamicBlock* modules are imported by
// model.ts so a circular import would leave this reference `undefined`. The
// structural shape (x: number, y: number) must stay identical to model.ts'
// drawingPointSchema.
export const drawingPointSchema = z.object({
  x: z.number(),
  y: z.number(),
});
export type DrawingPoint = z.infer<typeof drawingPointSchema>;

// ─── Value Set (shared by Linear / Polar / XY / Rotation numeric grips) ───
//
// AutoCAD supports three grip-snap modes on numeric parameters:
//   - "none"      — free drag
//   - "increment" — snap to `start + n * increment`, bounded by [min, max]
//   - "list"      — snap to the closest entry of `listValues`
//
// Backwards compatibility: Tier 1 persisted the field names `min` / `max`
// (see `linearValueSetSchema` in the pre-split dynamicBlock.ts). We keep
// those names verbatim so existing JSON / DXF round-trips parse without a
// migration. Tier 2's new numeric parameters share this same schema.
export const valueSetSchema = z.object({
  mode: z.enum(["none", "increment", "list"]).default("none"),
  increment: z.number().default(0),
  min: z.number().optional(),
  max: z.number().optional(),
  listValues: z.array(z.number()).default([]),
});
export type ValueSet = z.infer<typeof valueSetSchema>;

// Backwards-compat type alias — retained for external consumers that
// reference `LinearValueSet` by name. The schema itself is unified as
// `valueSetSchema`; the Linear-specific wrapper `linearValueSetSchema`
// was removed because no module references it by that name.
export type LinearValueSet = ValueSet;

// ─── Base parameter ────────────────────────────────────────────────
//
// Fields shared by every parameter kind:
//   - id, name           — identity
//   - label              — pretty UI label
//   - description        — Properties-palette tooltip
//   - chainActions       — DYN-3 flag: actions on this parameter feed sequentially
//   - gripPosition       — optional grip placement override
//   - showGrip           — whether to render the grip at all
//   - showInProperties   — Phase N.5 (BPM): controls the AutoCAD parity
//                          "Show in Properties palette" column. When false
//                          the parameter still drives evaluator math but is
//                          hidden from the INSERT Properties palette so end
//                          users cannot edit it. AutoCAD default is `true`.
export const baseParameterSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  label: z.string().default(""),
  description: z.string().default(""),
  chainActions: z.boolean().default(false),
  gripPosition: drawingPointSchema.optional(),
  showGrip: z.boolean().default(true),
  // Phase N.5 — optional rather than required-with-default so existing
  // test fixtures and inline TypeScript constructions of DynamicParameter
  // keep compiling without explicitly setting `showInProperties: true`.
  // Consumers (BPM dialog, mutation layer) treat `undefined` as `true`
  // — the AutoCAD default.
  showInProperties: z.boolean().optional(),
});

// ─── Base action ───────────────────────────────────────────────────
//
// Every action references:
//   - parameterId — the driving parameter
//   - selectionEntityIds — entity ids (within the block) this action transforms
//   - description — Phase N.5 (BPM): free-form description shown in the
//                   Block Properties Manager. Optional so legacy fixtures
//                   keep compiling; defaults to undefined.
// The `name` field is optional UI-friendly display — defaults to the id if
// empty.
export const baseActionSchema = z.object({
  id: z.string().min(1),
  name: z.string().default(""),
  description: z.string().optional(),
  parameterId: z.string().min(1),
  selectionEntityIds: z.array(z.string()).default([]),
});
